import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {ClickAwayListener, InputBase} from "@material-ui/core";
import { withStyles, WithStyles } from "@material-ui/core";
import { createStyles } from "@material-ui/core/styles";
import { CancelOutlined as Clear, Search as SearchIcon } from "@material-ui/icons";
// @ts-expect-error lodash/debounce has no typings in this workspace
import debounce from "lodash/debounce";

import appConstants from "@appConstants";

const styles = (theme: any) =>
  createStyles({
    headerIconStyles: {
      padding: "10px 10px",
      cursor: "pointer",
    },
    iconLayout: {
      float: "left",
      color: theme.iconColor,
      "&>span": {
        padding: "10px 10px",
        color: theme.iconColor,
      },
    },
    actionIcon: {
      fontSize: theme.headerTextFontSize,
    },
    clearIcon: {
      paddingRight: "5px",
    },
    iconVisible: {
      visibility: "visible",
      color: theme.iconColor,
      cursor: "pointer",
      paddingRight: "5px",
    },
    /** ClickAwayListener child: must float so GL header icons stay on one row */
    searchBarClickAwayRoot: {
      float: "left",
    },
    /** SearchBar expanded: narrow float anchor; panel absolute right:0 grows width left (no header wrap) */
    searchExpandAnchor: {
      float: "left",
      position: "relative",
      marginRight: 10,
      marginTop: 2,
      marginLeft: 0,
      width: 44,
      zIndex: 2,
      [theme.breakpoints.up("sm")]: {
        marginLeft: theme.spacing.unit,
      },
    },
    searchExpandedPanel: {
      position: "absolute",
      right: 0,
      top: 0,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      boxSizing: "border-box",
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.searchBGColor,
      overflow: "hidden",
      "&:hover": {
        backgroundColor: theme.searchHoverBGColor,
      },
      transition: theme.transitions.create("width", {
        duration: theme.transitions.duration.shorter,
      }),
      width: 220,
      [theme.breakpoints.up("sm")]: {
        width: 132,
        "&:focus-within": {
          width: 248,
        },
      },
      /** Viewports ~14" and below: cap focused (expanded) width; wider screens keep 248px above. */
      "@media (max-width: 1700px)": {
        [theme.breakpoints.up("sm")]: {
          "&:focus-within": {
            width: 200,
          },
        },
      },
    },
    searchExpandedLeadingIcon: {
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: theme.spacing.unit * 5,
      minHeight: 30,
      pointerEvents: "none",
    },
    searchExpandedInputRoot: {
      flex: 1,
      minWidth: 0,
      height: 30,
      fontSize: theme.headerTextFontSize,
      color: theme.iconColor,
    },
    searchExpandedInput: {
      paddingTop: theme.spacing.unit,
      paddingRight: theme.spacing.unit,
      paddingBottom: theme.spacing.unit,
      paddingLeft: theme.spacing.unit,
      width: "100%",
    },
    searchClearIconHidden: {
      display: "none",
    },
  });

type OwnProps = {
  eventHub: { emit: (headerId: string, searchText: string) => void };
  headerId: string;
};

type SearchBarProps = OwnProps & WithStyles<typeof styles>;

const SearchBar = ({ classes, eventHub, headerId }: SearchBarProps) => {
  const [searchText, setSearchText] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const searchTextRef = useRef("");
  searchTextRef.current = searchText;

  const latestRef = useRef({ eventHub, headerId });
  latestRef.current = { eventHub, headerId };

  const debouncedEmit = useMemo(
    () =>
      debounce((text: string) => {
        latestRef.current.eventHub.emit(latestRef.current.headerId, text);
      }, appConstants.SEARCH_DEBOUNCE_DELAY),
    []
  );

  useEffect(() => {
    return () => {
      latestRef.current.eventHub.emit(latestRef.current.headerId, "");
      debouncedEmit.cancel();
    };
  }, [debouncedEmit]);

  useEffect(() => {
    if (isExpanded) {
      inputRef.current?.focus();
    }
  }, [isExpanded]);

  const expandSearch = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const emitSearchTextEvent = useCallback((text: string) => {
    eventHub.emit(headerId, text);
  }, [eventHub, headerId]);

  const onSearchTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      debouncedEmit(next);
      setSearchText(next);
    },
    [debouncedEmit]
  );

  const onClear = useCallback(() => {
    setSearchText("");
    debouncedEmit.cancel();
    emitSearchTextEvent("");
    queueMicrotask(() => inputRef.current?.focus());
  }, [debouncedEmit, emitSearchTextEvent]);

  const onClickAway = useCallback(() => {
    if (!searchTextRef.current?.trim()) {
      setIsExpanded(false);
    }
  }, []);

  const handleCollapsedKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") expandSearch();
    },
    [expandSearch]
  );

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <div className={classes.searchBarClickAwayRoot}>
        {isExpanded ? (
          <div className={`${classes.searchExpandAnchor} ${classes.iconLayout} glHeaderIcons`}>
            <div className={classes.searchExpandedPanel}>
              <div className={classes.searchExpandedLeadingIcon}>
                <SearchIcon className={classes.actionIcon} />
              </div>
              <InputBase
                placeholder="Search…"
                classes={{
                  root: classes.searchExpandedInputRoot,
                  input: classes.searchExpandedInput,
                }}
                onChange={onSearchTextChange}
                value={searchText}
                inputRef={inputRef}
                endAdornment={
                  <Clear
                    data-testid="search-bar-clear"
                    classes={{
                      root: `${classes.clearIcon} ${
                        searchText ? classes.iconVisible : classes.searchClearIconHidden
                      }`,
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={onClear}
                  />
                }
              />
            </div>
          </div>
        ) : (
          <div
            className={`${classes.headerIconStyles} ${classes.iconLayout} glHeaderIcons`}
            onClick={expandSearch}
            role="button"
            tabIndex={0}
            onKeyDown={handleCollapsedKeyDown}
          >
            <SearchIcon className={classes.actionIcon} />
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default withStyles(styles)(memo(SearchBar));
